import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import { XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, AreaChart, BarChart, Bar } from 'recharts';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

// ===================== FUNÇÕES DE MÁSCARA =====================

const aplicarMascaraCPF = (valor) => {
  const nums = valor.replace(/\D/g, '').slice(0, 11);
  return nums
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
};

const aplicarMascaraTelefone = (valor) => {
  const nums = valor.replace(/\D/g, '').slice(0, 11);
  if (nums.length <= 10) {
    return nums
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{4})(\d{1,4})$/, '$1-$2');
  }
  return nums
    .replace(/(\d{2})(\d)/, '($1) $2')
    .replace(/(\d{5})(\d{1,4})$/, '$1-$2');
};

const aplicarMascaraMoeda = (valor) => {
  const nums = valor.replace(/\D/g, '');
  if (!nums) return '';
  const numero = parseInt(nums, 10) / 100;
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  }).format(numero);
};

const removerMascaraCPF = (valor) => valor ? valor.replace(/\D/g, '') : '';
const removerMascaraTelefone = (valor) => valor ? valor.replace(/\D/g, '') : '';
const removerMascaraMoeda = (valor) => {
  if (!valor) return null;
  const limpo = valor.replace(/[^\d,]/g, '').replace(',', '.');
  return parseFloat(limpo) || null;
};

const validarCPF = (valor) => {
  const nums = valor.replace(/\D/g, '');

  if (nums.length !== 11) return false;

  // Rejeita sequências como 111.111.111-11
  if (/^(\d)\1{10}$/.test(nums)) return false;

  const calcDigito = (base, pesoInicial) =>
    base.split('').reduce((acc, d, i) => acc + Number(d) * (pesoInicial - i), 0);

  const resto = (soma) => {
    const r = (soma * 10) % 11;
    return r === 10 || r === 11 ? 0 : r;
  };

  const d1 = resto(calcDigito(nums.slice(0, 9), 10));
  const d2 = resto(calcDigito(nums.slice(0, 10), 11));

  return d1 === Number(nums[9]) && d2 === Number(nums[10]);
};

// Opções que a API devolve mas a tela não oferece: { codigoBanco: [codigoOpcao] }
const OPCOES_OCULTAS = {
  santander: ['price'],
};

// ==============================================================

const SimuladorFinanciamentoAPI = () => {
  const [nomeCompleto, setNomeCompleto] = useState('');
  const [cpf, setCpf] = useState('');
  const [cpfValido, setCpfValido] = useState(null); // null = não validado ainda, true/false
  const [telefone, setTelefone] = useState('');
  const [email, setEmail] = useState('');
  const [renda, setRenda] = useState('');
  const [valorImovel, setValorImovel] = useState('');
  const [valorFinanciado, setValorFinanciado] = useState('');
  const [prazoMeses, setPrazoMeses] = useState(null);
  const [indicacaoParceira, setIndicacaoParceira] = useState('');
  const [dataNascimento, setDataNascimento] = useState(null);
  const [mostrarTabela, setMostrarTabela] = useState(false);
  const [bancoSelecionado, setBancoSelecionado] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [resultado, setResultado] = useState(null);
  const comparativoRef = React.useRef(null);
  const [paginaAtual, setPaginaAtual] = useState('simulador'); // 'simulador' ou 'evolucao'
  const [parceiroid, setParceiroId] = useState('andalafat');

  // Mapeamento de logos por nome do banco
  const logosBancos = {
    
    'Santander': 'img/logos/santander.png',
    'Itau': 'img/logos/itau.png',
    'Itaú': 'img/logos/itau.png',
    'Inter': 'img/logos/inter.png',
    'Bradesco': 'img/logos/bradesco.png',
    'Caixa': 'img/logos/caixa.png',
    'BRB': 'img/logos/brb.png',
  };
  // const [taxasSelecionadas, setTaxasSelecionadas] = useState({
  //   BRB: 10.79,
  //   Bradesco: 11.70,
  //   Caixa: 11.29,
  //   Inter: 15.00,
  //   Santander: 11.79,
  //   "Itaú": 11.70,
  // });

  const [taxasSelecionadas, setTaxasSelecionadas] = useState({
  });
  const [camposInvalidos, setCamposInvalidos] = useState({});
  const [taxaInvalida, setTaxaInvalida] = useState(false);

  // URL da API - ajuste conforme necessário
  //const API_URL = 'https://localhost:7285/api/Simulador';
  const API_URL = 'https://multibanco.azurewebsites.net/api/Simulador';

  // Taxas vindas da API (/taxas), já filtradas pelo parceiro
  const [bancosApi, setBancosApi] = useState([]);
  const [vigenteDe, setVigenteDe] = useState(null);
  const [taxasLoading, setTaxasLoading] = useState(true);
  const [taxasErro, setTaxasErro] = useState(null);

  // O logo é do front, escolhido pelo código do banco
  const logosPorCodigo = {
    itau: 'img/logos/itau.png',
    bradesco: 'img/logos/bradesco.png',
    santander: 'img/logos/santander.png',
    inter: 'img/logos/inter.png',
    caixa: 'img/logos/caixa.png',
    brb: 'img/logos/brb.png',
  };
  const logoDoBanco = (codigo) => logosPorCodigo[(codigo || '').toLowerCase()];

  const formatarTaxaAnual = (valor) => Number(valor).toFixed(2).replace('.', ',');

  const carregarTaxas = useCallback(async () => {
    setTaxasLoading(true);
    setTaxasErro(null);
    try {
      const response = await fetch(`${API_URL}/taxas?parceiroId=${encodeURIComponent(parceiroid)}`);
      if (!response.ok) throw new Error('Não foi possível carregar as taxas.');
      const data = await response.json();
      const bancos = (data.bancos || [])
        .map(b => ({
          ...b,
          opcoes: b.opcoes.filter(o => !(OPCOES_OCULTAS[b.codigo] || []).includes(o.codigo)),
        }))
        .filter(b => b.opcoes.length > 0);
      setBancosApi(bancos);
      setVigenteDe(bancos.map(b => b.vigenteDe).filter(Boolean).sort().pop() || null);
      // Descarta seleções que não existem mais na lista atual
      setTaxasSelecionadas(prev => {
        const validas = {};
        Object.entries(prev).forEach(([codBanco, codOpcao]) => {
          const banco = bancos.find(b => b.codigo === codBanco);
          if (banco && banco.opcoes.some(o => o.codigo === codOpcao)) validas[codBanco] = codOpcao;
        });
        return validas;
      });
      return true;
    } catch (err) {
      setTaxasErro(err.message || 'Não foi possível carregar as taxas.');
      return false;
    } finally {
      setTaxasLoading(false);
    }
  }, [API_URL, parceiroid]);

  useEffect(() => {
    carregarTaxas();
  }, [carregarTaxas]);

  const opcoesEscolhidas = bancosApi.flatMap(b =>
    b.opcoes.filter(o => taxasSelecionadas[b.codigo] === o.codigo)
  );
  // Prazo máximo = menor prazo entre as opções escolhidas (420 se nenhuma)
  const prazoMaximo = opcoesEscolhidas.length > 0
    ? Math.min(...opcoesEscolhidas.map(o => o.prazoMaximoMeses))
    : 420;
  const temIndexadorNaoTR = bancosApi.some(b => b.opcoes.some(o => o.indexador !== 'TR'));

  const simularFinanciamento = async () => {
    // Valida preenchimento dos campos obrigatórios
    const camposObrigatorios = [
      { chave: 'nomeCompleto', valor: nomeCompleto },
      { chave: 'cpf', valor: cpf },
      { chave: 'dataNascimento', valor: dataNascimento },
      { chave: 'telefone', valor: telefone },
      { chave: 'email', valor: email },
      { chave: 'renda', valor: renda },
      { chave: 'valorImovel', valor: valorImovel },
      { chave: 'valorFinanciado', valor: valorFinanciado },
      { chave: 'prazoMeses', valor: prazoMeses },
      { chave: 'indicacaoParceira', valor: indicacaoParceira },
    ];

    const novosErros = {};
    camposObrigatorios.forEach(({ chave, valor }) => {
      if (!valor) {
        novosErros[chave] = true;
      }
    });
    setCamposInvalidos(novosErros);

    const temTaxaSelecionada = Object.keys(taxasSelecionadas).length > 0;
    setTaxaInvalida(!temTaxaSelecionada);

    if (Object.keys(novosErros).length > 0) {
      setError('Preencha todos os campos obrigatórios.');
      return;
    }

    if (!temTaxaSelecionada) {
      setError('Selecione ao menos uma taxa de banco.');
      return;
    }

    if (prazoMeses > prazoMaximo) {
      setError(`O prazo máximo para as opções escolhidas é de ${prazoMaximo} meses.`);
      return;
    }

    // Valida CPF antes de chamar a API
    if (cpf && !validarCPF(cpf)) {
      setCpfValido(false);
      setError('CPF inválido. Verifique o número digitado.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/simular-multibanco-parceiro`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          valorImovel: removerMascaraMoeda(valorImovel),
          valorFinanciado: removerMascaraMoeda(valorFinanciado),
          prazoMeses,
          dataNascimento,
          opcoesPorBanco: taxasSelecionadas,
          nomeCompleto,
          cpf: removerMascaraCPF(cpf),
          telefone: removerMascaraTelefone(telefone),
          email,
          renda: removerMascaraMoeda(renda),
          parceiroid,
          indicacaoParceira
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status >= 500) {
          throw new Error('Erro inesperado ao simular. Tente novamente.');
        }
        if (errorData.detail && errorData.detail.startsWith('Opção de taxa')) {
          // A lista de taxas mudou desde que a tela abriu
          await carregarTaxas();
          throw new Error('As taxas foram atualizadas. Revise a seleção e simule novamente.');
        }
        throw new Error(errorData.detail || 'Erro ao simular financiamento');
      }

      const data = await response.json();
      setResultado(data);
      setBancoSelecionado(data.simulacoes[0]?.banco || null);
    } catch (err) {
      setError(err.message);
      console.error('Erro:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatarMoeda = (valor) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor);
  };

  const formatarPercentual = (valor) => {
    return `${valor.toFixed(2)}%`;
  };

  const simulacaoAtual = resultado?.simulacoes?.find(s => s.banco === bancoSelecionado);

  const dadosGrafico = simulacaoAtual?.parcelas
    ? simulacaoAtual.parcelas
      .filter(p => p.numero > 0)
      .filter((_, idx, arr) =>
        idx % (prazoMeses > 60 ? 6 : 3) === 0 ||
        idx === 0 ||
        idx === arr.length - 1
      )
    : [];

  const dadosComparativo = resultado?.simulacoes?.map(s => ({
    banco: s.nomeBanco,
    primeiraParcela: s.informacoes.primeiraParcela,
    ultimaParcela: s.informacoes.ultimaParcela,
    totalJuros: s.totais.totalJuros,
    totalAPagar: s.totais.totalAPagar,
    taxaJuros: s.taxaJurosAnual
  })) || [];

  // Calcular economia entre bancos
  const calcularEconomia = () => {
    if (!resultado?.simulacoes || resultado.simulacoes.length < 2) return null;
    const totais = resultado.simulacoes.map(s => s.totais.totalAPagar);
    const menor = Math.min(...totais);
    const maior = Math.max(...totais);
    return maior - menor;
  };

  const economia = calcularEconomia();

 const exportarPDF = async () => {
  if (!comparativoRef.current || !simulacaoAtual) return;
  
  try {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    
    // ============ PÁGINA 1 - COMPARATIVO ============
    // Header
    pdf.setFillColor(0, 58, 93);
    pdf.rect(0, 0, pdfWidth, 30, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(18);
    pdf.text('Simulador MultiBanco', pdfWidth / 2, 12, { align: 'center' });
    pdf.setFontSize(10);
    pdf.text(`Gerado em: ${new Date().toLocaleDateString('pt-BR')} às ${new Date().toLocaleTimeString('pt-BR')}`, pdfWidth / 2, 20, { align: 'center' });
    pdf.text(`Valor Financiado: ${formatarMoeda(valorFinanciado)} | Prazo: ${prazoMeses} meses`, pdfWidth / 2, 26, { align: 'center' });
    
    // Capturar comparativo
    const canvasComparativo = await html2canvas(comparativoRef.current, {
      scale: 2,
      backgroundColor: '#1e293b',
      logging: false
    });
    
    const imgComparativo = canvasComparativo.toDataURL('image/png');
    const imgHeight = (canvasComparativo.height * (pdfWidth - 20)) / canvasComparativo.width;
    
    pdf.addImage(imgComparativo, 'PNG', 10, 35, pdfWidth - 20, imgHeight);
    
    // ============ PÁGINA 2+ - PARCELAS ============
    pdf.addPage();
    
    // Header página parcelas
    pdf.setFillColor(0, 58, 93);
    pdf.rect(0, 0, pdfWidth, 20, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(14);
    pdf.text(`Evolução das Parcelas - ${simulacaoAtual.nomeBanco}`, pdfWidth / 2, 13, { align: 'center' });
    
    // Tabela de parcelas
    pdf.setFontSize(8);
    let yPos = 30;
    const colWidths = [12, 28, 28, 22, 22, 18, 28, 32];
    const headers = ['Nº', 'Amortização', 'Juros', 'MIP', 'DFI', 'TGC', 'Parcela', 'Saldo Devedor'];
    
    // Header da tabela
    pdf.setFillColor(0, 58, 93);
    pdf.rect(10, yPos - 5, pdfWidth - 20, 8, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(7);
    let xPos = 12;
    headers.forEach((header, i) => {
      pdf.text(header, xPos, yPos);
      xPos += colWidths[i];
    });
    
    yPos += 6;
    pdf.setTextColor(50, 50, 50);
    
    // Linhas da tabela
    simulacaoAtual.parcelas.forEach((parcela, idx) => {
      if (yPos > pageHeight - 20) {
        pdf.addPage();
        yPos = 20;
        
        // Header da tabela na nova página
        pdf.setFillColor(0, 58, 93);
        pdf.rect(10, yPos - 5, pdfWidth - 20, 8, 'F');
        pdf.setTextColor(255, 255, 255);
        xPos = 12;
        headers.forEach((header, i) => {
          pdf.text(header, xPos, yPos);
          xPos += colWidths[i];
        });
        yPos += 6;
        pdf.setTextColor(50, 50, 50);
      }
      
      // Cor de fundo alternada
      if (idx % 2 === 0) {
        pdf.setFillColor(245, 245, 245);
        pdf.rect(10, yPos - 4, pdfWidth - 20, 6, 'F');
      }
      
      xPos = 12;
      const valores = [
        parcela.numero === 0 ? '0' : parcela.numero.toString(),
        formatarMoeda(parcela.amortizacao),
        formatarMoeda(parcela.juros),
        formatarMoeda(parcela.mip),
        formatarMoeda(parcela.dfi),
        formatarMoeda(parcela.tgc),
        formatarMoeda(parcela.valorParcela),
        formatarMoeda(parcela.saldoDevedor)
      ];
      
      valores.forEach((valor, i) => {
        pdf.text(valor, xPos, yPos);
        xPos += colWidths[i];
      });
      
      yPos += 6;
    });
    
    // Totais
    yPos += 5;
    pdf.setFillColor(0, 58, 93);
    pdf.rect(10, yPos - 4, pdfWidth - 20, 10, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(9);
    pdf.text(`TOTAIS:  Juros: ${formatarMoeda(simulacaoAtual.totais.totalJuros)}  |  Seguros: ${formatarMoeda(simulacaoAtual.totais.totalMIP + simulacaoAtual.totais.totalDFI)}  |  Total a Pagar: ${formatarMoeda(simulacaoAtual.totais.totalAPagar)}`, 15, yPos + 2);
    
    // Footer em todas as páginas
    const totalPages = pdf.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      pdf.setPage(i);
      pdf.setFontSize(8);
      pdf.setTextColor(150, 150, 150);
      pdf.text('Simulação para fins informativos. Sujeito à análise de crédito. | Monetare 2026', pdfWidth / 2, pageHeight - 5, { align: 'center' });
      pdf.text(`Página ${i} de ${totalPages}`, pdfWidth - 15, pageHeight - 5, { align: 'right' });
    }
    
    pdf.save(`simulacao-${simulacaoAtual.nomeBanco.split(' ')[0]}-${new Date().toISOString().split('T')[0]}.pdf`);
    
  } catch (error) {
    console.error('Erro ao gerar PDF:', error);
    alert('Erro ao gerar PDF. Tente novamente.');
  }
};

const exportarPDFEvolucao = async () => {
  if (!resultado?.simulacoes) return;
  
  try {
    const pdf = new jsPDF('l', 'mm', 'a4'); // Landscape
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const bancos = resultado.simulacoes;
    const numBancos = bancos.length;
    
    // ============ PÁGINA 1 - RESUMO ============
    // Header
    pdf.setFillColor(0, 58, 93);
    pdf.rect(0, 0, pdfWidth, 25, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(16);
    pdf.text('Simulação de Financiamento Imobiliário - Comparativo MultiBanco', pdfWidth / 2, 10, { align: 'center' });
    pdf.setFontSize(9);
    pdf.text(`Gerado em: ${new Date().toLocaleDateString('pt-BR')} às ${new Date().toLocaleTimeString('pt-BR')}`, pdfWidth / 2, 17, { align: 'center' });
    pdf.text(`Valor Financiado: ${formatarMoeda(valorFinanciado)} | Prazo: ${prazoMeses} meses | Valor do Imóvel: ${formatarMoeda(valorImovel)}`, pdfWidth / 2, 22, { align: 'center' });
    
    let yPos = 35;
    
    // Tabela Resumo
    pdf.setFontSize(11);
    pdf.setTextColor(0, 58, 93);
    pdf.text('Resumo da Simulação', 15, yPos);
    yPos += 8;
    
    // Header da tabela resumo
    const colWidthResumo = (pdfWidth - 60) / numBancos;
    pdf.setFillColor(0, 58, 93);
    pdf.rect(15, yPos - 5, pdfWidth - 30, 8, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(8);
    pdf.text('Resumo', 17, yPos);
    bancos.forEach((b, i) => {
      pdf.text(b.nomeBanco.split(' ')[0], 50 + (i * colWidthResumo), yPos);
    });
    yPos += 8;
    
    // Linhas do resumo
    const linhasResumo = [
      { label: 'Taxa de Juros', valores: bancos.map(b => `${b.taxaJurosAnual.toFixed(2)}%`) },
      { label: 'Primeira Parcela', valores: bancos.map(b => formatarMoeda(b.informacoes.primeiraParcela)) },
      { label: 'Última Parcela', valores: bancos.map(b => formatarMoeda(b.informacoes.ultimaParcela)) },
      { label: 'Total Juros', valores: bancos.map(b => formatarMoeda(b.totais.totalJuros)) },
      { label: 'Total Seguros', valores: bancos.map(b => formatarMoeda(b.totais.totalMIP + b.totais.totalDFI)) },
      { label: 'Total a Pagar', valores: bancos.map(b => formatarMoeda(b.totais.totalAPagar)) },
    ];
    
    const menorTotal = Math.min(...bancos.map(b => b.totais.totalAPagar));
    
    linhasResumo.forEach((linha, idx) => {
      if (idx % 2 === 0) {
        pdf.setFillColor(240, 240, 240);
        pdf.rect(15, yPos - 4, pdfWidth - 30, 7, 'F');
      }
      pdf.setTextColor(100, 100, 100);
      pdf.text(linha.label, 17, yPos);
      
      linha.valores.forEach((valor, i) => {
        const isMelhor = linha.label === 'Total a Pagar' && bancos[i].totais.totalAPagar === menorTotal;
        if (isMelhor) {
          pdf.setTextColor(16, 185, 129);
          pdf.text(valor + ' ✓', 50 + (i * colWidthResumo), yPos);
        } else {
          pdf.setTextColor(50, 50, 50);
          pdf.text(valor, 50 + (i * colWidthResumo), yPos);
        }
      });
      yPos += 7;
    });
    
    // ============ SOMA POR PERÍODO ============
    yPos += 10;
    pdf.setFontSize(11);
    pdf.setTextColor(0, 58, 93);
    pdf.text('Soma de Valores por Período (12 meses)', 15, yPos);
    yPos += 8;
    
    // Header
    pdf.setFillColor(0, 58, 93);
    pdf.rect(15, yPos - 5, pdfWidth - 30, 8, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(8);
    pdf.text('Período', 17, yPos);
    bancos.forEach((b, i) => {
      pdf.text(b.nomeBanco.split(' ')[0], 50 + (i * colWidthResumo), yPos);
    });
    yPos += 8;
    
    // Linhas por período
    const numPeriodos = Math.ceil(prazoMeses / 12);
    for (let p = 0; p < numPeriodos; p++) {
      const inicio = p * 12 + 1;
      const fim = Math.min((p + 1) * 12, prazoMeses);
      
      if (yPos > pageHeight - 20) {
        pdf.addPage();
        yPos = 20;
      }
      
      if (p % 2 === 0) {
        pdf.setFillColor(240, 240, 240);
        pdf.rect(15, yPos - 4, pdfWidth - 30, 7, 'F');
      }
      
      pdf.setTextColor(100, 100, 100);
      pdf.text(`Parcela ${String(inicio).padStart(3, '0')} - ${String(fim).padStart(3, '0')}`, 17, yPos);
      
      const somas = bancos.map(b => 
        b.parcelas.filter(pa => pa.numero >= inicio && pa.numero <= fim)
          .reduce((acc, pa) => acc + pa.valorParcela, 0)
      );
      const menorSoma = Math.min(...somas.filter(s => s > 0));
      
      somas.forEach((soma, i) => {
        const isMenor = soma === menorSoma && soma > 0;
        pdf.setTextColor(isMenor ? 16 : 50, isMenor ? 185 : 50, isMenor ? 129 : 50);
        pdf.text(formatarMoeda(soma), 50 + (i * colWidthResumo), yPos);
      });
      yPos += 7;
    }
    
    // ============ PÁGINAS DE EVOLUÇÃO DAS PARCELAS ============
    pdf.addPage();
    
    // Filtrar parcelas (remover parcela 0)
    const parcelasFiltradas = bancos.map(b => ({
      ...b,
      parcelas: b.parcelas.filter(p => p.numero > 0)
    }));

    const maxParcelas = Math.max(...parcelasFiltradas.map(b => b.parcelas?.length || 0));
    const parcelasPorPagina = 35;
    let parcelaAtual = 0;
    
    while (parcelaAtual < maxParcelas) {
      if (parcelaAtual > 0) {
        pdf.addPage();
      }
      
      // Header
      pdf.setFillColor(0, 58, 93);
      pdf.rect(0, 0, pdfWidth, 15, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(12);
      pdf.text('Evolução das Parcelas - Todos os Bancos', pdfWidth / 2, 10, { align: 'center' });
      
      yPos = 25;
      
      // Header da tabela
      pdf.setFillColor(0, 58, 93);
      pdf.rect(10, yPos - 5, pdfWidth - 20, 8, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(8);
      
      const colWidthParcela = (pdfWidth - 40) / numBancos;
      pdf.text('Parcela', 12, yPos);
      parcelasFiltradas.forEach((b, i) => {
        pdf.text(b.nomeBanco.split(' ')[0], 35 + (i * colWidthParcela), yPos);
      });
      yPos += 8;
      
      // Linhas das parcelas
      const parcelaFim = Math.min(parcelaAtual + parcelasPorPagina, maxParcelas);
      
      for (let idx = parcelaAtual; idx < parcelaFim; idx++) {
        const numeroParcela = parcelasFiltradas[0]?.parcelas[idx]?.numero ?? idx;
        
        if (idx % 2 === 0) {
          pdf.setFillColor(245, 245, 245);
          pdf.rect(10, yPos - 4, pdfWidth - 20, 6, 'F');
        }
        
        pdf.setTextColor(100, 100, 100);
        pdf.text(numeroParcela === 0 ? '0' : String(numeroParcela).padStart(3, '0'), 12, yPos);
        
        const valoresParcela = parcelasFiltradas.map(b => b.parcelas[idx]?.valorParcela || 0);
        const menorValor = Math.min(...valoresParcela.filter(v => v > 0));
        
        parcelasFiltradas.forEach((b, i) => {
          const parcela = b.parcelas[idx];
          const isMenor = parcela?.valorParcela === menorValor && parcela?.valorParcela > 0;
          pdf.setTextColor(isMenor ? 16 : 50, isMenor ? 185 : 50, isMenor ? 129 : 50);
          pdf.text(parcela ? formatarMoeda(parcela.valorParcela) : '-', 35 + (i * colWidthParcela), yPos);
        });
        
        yPos += 6;
      }
      
      parcelaAtual = parcelaFim;
    }
    
    // Footer em todas as páginas
    const totalPages = pdf.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      pdf.setPage(i);
      pdf.setFontSize(8);
      pdf.setTextColor(150, 150, 150);
      pdf.text('Simulação para fins informativos. Sujeito à análise de crédito. | Monetare 2026', pdfWidth / 2, pageHeight - 5, { align: 'center' });
      pdf.text(`Página ${i} de ${totalPages}`, pdfWidth - 15, pageHeight - 5, { align: 'right' });
    }
    
    pdf.save(`simulacao-comparativo-${new Date().toISOString().split('T')[0]}.pdf`);
    
  } catch (error) {
    console.error('Erro ao gerar PDF:', error);
    alert('Erro ao gerar PDF. Tente novamente.');
  }
};

// Página de Evolução Comparativa
const PaginaEvolucao = () => {
  const bancosOriginal = resultado?.simulacoes || [];
  const bancos = bancosOriginal.map(b => ({
    ...b,
    parcelas: b.parcelas.filter(p => p.numero > 0)
  }));
  const maxParcelas = Math.max(...bancos.map(b => b.parcelas?.length || 0));
    
  return (
    <div className="sim-page">
      <div style={{ maxWidth: '1600px', margin: '0 auto' }}>
        {/* Header */}
        <div className="evo-header">
          <div>
            <h1 style={{ fontSize: '1.8rem', fontWeight: '700', marginBottom: '8px' }}>
              📊 Evolução Comparativa das Parcelas
            </h1>
            <p style={{ color: '#94a3b8' }}>
              Valor Financiado: {formatarMoeda(valorFinanciado)} | Prazo: {prazoMeses} meses
            </p>
          </div>
          <div className="evo-header-btns">
            <button
              onClick={() => setPaginaAtual('simulador')}
              style={{
                padding: '12px 24px',
                fontSize: '0.95rem',
                fontWeight: '600',
                background: 'linear-gradient(135deg, #60a5fa, #3b82f6)',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                cursor: 'pointer',
              }}
            >
              ← Voltar ao Simulador
            </button>
            <button
              onClick={exportarPDFEvolucao}
              style={{
                padding: '12px 24px',
                fontSize: '0.95rem',
                fontWeight: '600',
                background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                cursor: 'pointer',
              }}
            >
              📄 Exportar PDF
            </button>
          </div>
        </div>

        {/* Resumo dos Bancos */}
        <div className="evo-tabela-wrap" style={{
          background: 'rgba(255, 255, 255, 0.03)',
          borderRadius: '16px',
          padding: '24px',
          marginBottom: '30px',
          border: '1px solid rgba(255, 255, 255, 0.08)',
        }}>
          <h2 style={{ fontSize: '1.2rem', marginBottom: '20px', color: '#e2e8f0' }}>
            Resumo da Simulação
          </h2>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ background: 'rgba(96, 165, 250, 0.2)' }}>
                <th style={{ padding: '12px', textAlign: 'left', color: '#e2e8f0' }}>Resumo</th>
                {bancos.map((b, i) => (
                  <th key={i} style={{ padding: '12px', textAlign: 'center', color: '#60a5fa' }}>
                    {b.nomeBanco.split(' ')[0]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                <td style={{ padding: '12px', color: '#94a3b8' }}>Taxa de Juros</td>
                {bancos.map((b, i) => (
                  <td key={i} style={{ padding: '12px', textAlign: 'center', color: '#f59e0b', fontWeight: '600' }}>
                    {b.taxaJurosAnual.toFixed(2)}%
                  </td>
                ))}
              </tr>
              <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                <td style={{ padding: '12px', color: '#94a3b8' }}>Primeira Parcela</td>
                {bancos.map((b, i) => (
                  <td key={i} style={{ padding: '12px', textAlign: 'center', color: '#60a5fa' }}>
                    {formatarMoeda(b.informacoes.primeiraParcela)}
                  </td>
                ))}
              </tr>
              <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                <td style={{ padding: '12px', color: '#94a3b8' }}>Última Parcela</td>
                {bancos.map((b, i) => (
                  <td key={i} style={{ padding: '12px', textAlign: 'center', color: '#a78bfa' }}>
                    {formatarMoeda(b.informacoes.ultimaParcela)}
                  </td>
                ))}
              </tr>
              <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                <td style={{ padding: '12px', color: '#94a3b8' }}>Total a Pagar</td>
                {bancos.map((b, i) => {
                  const menorTotal = Math.min(...bancos.map(x => x.totais.totalAPagar));
                  const isMelhor = b.totais.totalAPagar === menorTotal;
                  return (
                    <td key={i} style={{ 
                      padding: '12px', 
                      textAlign: 'center', 
                      color: isMelhor ? '#10b981' : '#e2e8f0',
                      fontWeight: '700',
                      background: isMelhor ? 'rgba(16, 185, 129, 0.1)' : 'transparent'
                    }}>
                      {formatarMoeda(b.totais.totalAPagar)}
                      {isMelhor && ' ✓'}
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>

        {/* Tabela de Evolução das Parcelas */}
        <div className="evo-tabela-wrap" style={{
          background: 'rgba(255, 255, 255, 0.03)',
          borderRadius: '16px',
          padding: '24px',
          border: '1px solid rgba(255, 255, 255, 0.08)',
        }}>
          <h2 style={{ fontSize: '1.2rem', marginBottom: '20px', color: '#e2e8f0' }}>
            Evolução das Parcelas - Todos os Bancos
          </h2>
          <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
              <thead style={{ position: 'sticky', top: 0, background: '#1e293b', zIndex: 10 }}>
                <tr style={{ background: 'rgba(96, 165, 250, 0.2)' }}>
                  <th style={{ 
                    padding: '12px 8px', 
                    textAlign: 'center', 
                    color: '#e2e8f0',
                    position: 'sticky',
                    left: 0,
                    background: 'rgba(96, 165, 250, 0.3)',
                    minWidth: '60px'
                  }}>
                    Parcela
                  </th>
                  {bancos.map((b, i) => (
                    <th key={i} style={{ 
                      padding: '12px 8px', 
                      textAlign: 'center', 
                      color: '#60a5fa',
                      minWidth: '100px'
                    }}>
                      {b.nomeBanco.split(' ')[0]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: maxParcelas }, (_, idx) => {
                  const numeroParcela = bancos[0]?.parcelas[idx]?.numero ?? idx;
                  return (
                    <tr 
                      key={idx} 
                      style={{ 
                        borderBottom: '1px solid rgba(255,255,255,0.05)',
                        background: idx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)'
                      }}
                    >
                      <td style={{ 
                        padding: '10px 8px', 
                        textAlign: 'center', 
                        color: '#94a3b8',
                        fontWeight: '600',
                        position: 'sticky',
                        left: 0,
                        background: idx % 2 === 0 ? '#1e293b' : '#232d3b'
                      }}>
                        {numeroParcela === 0 ? '0 (inicial)' : String(numeroParcela).padStart(3, '0')}
                      </td>
                      {bancos.map((b, i) => {
                        const parcela = b.parcelas[idx];
                        const valores = bancos.map(x => x.parcelas[idx]?.valorParcela || 0).filter(v => v > 0);
                        const menorValor = Math.min(...valores);
                        const isMenor = parcela?.valorParcela === menorValor && parcela?.valorParcela > 0;
                        
                        return (
                          <td key={i} style={{ 
                            padding: '10px 8px', 
                            textAlign: 'center',
                            color: isMenor ? '#10b981' : '#e2e8f0',
                            fontWeight: isMenor ? '600' : '400',
                            background: isMenor ? 'rgba(16, 185, 129, 0.1)' : 'transparent'
                          }}>
                            {parcela ? formatarMoeda(parcela.valorParcela) : '-'}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Soma por Período */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.03)',
          borderRadius: '16px',
          padding: '24px',
          marginTop: '30px',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          overflowX: 'auto'
        }}>
          <h2 style={{ fontSize: '1.2rem', marginBottom: '20px', color: '#e2e8f0' }}>
            Soma de Valores por Período (12 meses)
          </h2>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ background: 'rgba(96, 165, 250, 0.2)' }}>
                <th style={{ padding: '12px', textAlign: 'left', color: '#e2e8f0' }}>Período</th>
                {bancos.map((b, i) => (
                  <th key={i} style={{ padding: '12px', textAlign: 'center', color: '#60a5fa' }}>
                    {b.nomeBanco.split(' ')[0]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: Math.ceil(prazoMeses / 12) }, (_, periodoIdx) => {
                const inicio = periodoIdx * 12 + 1;
                const fim = Math.min((periodoIdx + 1) * 12, prazoMeses);
                
                const somasPorBanco = bancos.map(b => {
                  return b.parcelas
                    .filter(p => p.numero >= inicio && p.numero <= fim)
                    .reduce((acc, p) => acc + p.valorParcela, 0);
                });
                
                const menorSoma = Math.min(...somasPorBanco.filter(s => s > 0));
                
                return (
                  <tr key={periodoIdx} style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                    <td style={{ padding: '12px', color: '#94a3b8' }}>
                      Parcela {String(inicio).padStart(3, '0')} - {String(fim).padStart(3, '0')}
                    </td>
                    {somasPorBanco.map((soma, i) => {
                      const isMenor = soma === menorSoma && soma > 0;
                      return (
                        <td key={i} style={{ 
                          padding: '12px', 
                          textAlign: 'center',
                          color: isMenor ? '#10b981' : '#e2e8f0',
                          fontWeight: isMenor ? '600' : '400',
                          background: isMenor ? 'rgba(16, 185, 129, 0.1)' : 'transparent'
                        }}>
                          {formatarMoeda(soma)}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div style={{
          textAlign: 'center',
          marginTop: '40px',
          padding: '20px',
          color: '#64748b',
          fontSize: '0.85rem'
        }}>
          <p>Simulação para fins informativos. Sujeito à análise de crédito. | Monetare 2026</p>
        </div>
      </div>
    </div>
  );
};




//return principal
  return (
<>
    {paginaAtual === 'evolucao' && resultado ? (
      <PaginaEvolucao />
    ) : (
    <div className="sim-page">



      <div className="sim-container">
        {/* Header */}
        <div className="sim-header">
         
          <p>
            <img src="img/andalafat-simples-negativo.svg" alt="logo andalafat" className="logo" />
          </p>
          <p className="subtitulo">
            Compare ofertas de financiamento imobiliário de múltiplos bancos em um só lugar!
          </p>
        </div>

        {/* Formulário de Entrada */}
        <div className="sim-card-form">
          <div className="sim-fields-grid">
            {[
              { chave: 'nomeCompleto', label: 'Nome completo', value: nomeCompleto, setter: setNomeCompleto, type: 'text', placeholder: 'Nome completo' },
              { chave: 'cpf', label: 'CPF', value: cpf, setter: setCpf, type: 'text', placeholder: '000.000.000-00', mascara: 'cpf' },
              { chave: 'dataNascimento', label: 'Data de Nascimento', value: dataNascimento, setter: setDataNascimento, type: 'date' },
              { chave: 'telefone', label: 'Telefone', value: telefone, setter: setTelefone, type: 'text', placeholder: '(00) 00000-0000', mascara: 'telefone' },
              { chave: 'email', label: 'E-mail', value: email, setter: setEmail, type: 'text', placeholder: 'seu@email.com' },
              { chave: 'renda', label: 'Renda bruta', value: renda, setter: setRenda, type: 'text', placeholder: 'R$ 0,00', mascara: 'moeda' },
              { chave: 'valorImovel', label: 'Valor do Imóvel', value: valorImovel, setter: setValorImovel, type: 'text', placeholder: 'R$ 0,00', mascara: 'moeda' },
              { chave: 'valorFinanciado', label: 'Valor Financiamento', value: valorFinanciado, setter: setValorFinanciado, type: 'text', placeholder: 'R$ 0,00', mascara: 'moeda' },
              { chave: 'prazoMeses', label: 'Prazo', value: prazoMeses, setter: setPrazoMeses, suffix: ' meses', step: 12, max: prazoMaximo, type: 'number', placeholder: 'Ex: 360' },
              { chave: 'indicacaoParceira', label: 'Indicação Parceira', value: indicacaoParceira, setter: setIndicacaoParceira, type: 'text' }

            ].map((campo, idx) => {
              const isCpf = campo.mascara === 'cpf';
              const isInvalido = !!camposInvalidos[campo.chave];
              const borderColor = isCpf && cpfValido === false
                ? 'rgba(239, 68, 68, 0.8)'
                : isCpf && cpfValido === true
                  ? 'rgba(16, 185, 129, 0.8)'
                  : isInvalido
                    ? 'rgba(239, 68, 68, 0.8)'
                    : '#cbd5e1';

              return (
              <div key={idx} style={{ animation: `slideIn 0.5s ease-out both` }}>
                <label style={{
                  display: 'block',
                  marginBottom: '12px',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  color: '#334155',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  {campo.label}
                  <span style={{ color: '#ef4444' }}> *</span>
                  {isCpf && cpfValido === true && (
                    <span style={{ marginLeft: '8px', color: '#10b981', fontSize: '0.85rem', textTransform: 'none' }}>✓ válido</span>
                  )}
                  {isCpf && cpfValido === false && (
                    <span style={{ marginLeft: '8px', color: '#ef4444', fontSize: '0.85rem', textTransform: 'none' }}>✗ inválido</span>
                  )}
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={campo.type || 'text'}
                    value={campo.value ?? ''}
                    placeholder={campo.placeholder}
                    required
                    onChange={(e) => {
                      const raw = e.target.value;
                      if (camposInvalidos[campo.chave]) {
                        setCamposInvalidos(prev => ({ ...prev, [campo.chave]: false }));
                      }
                      if (campo.mascara === 'cpf') {
                        const mascarado = aplicarMascaraCPF(raw);
                        campo.setter(mascarado);
                        // Valida ao preencher completamente (14 chars = 000.000.000-00)
                        if (mascarado.length === 14) {
                          setCpfValido(validarCPF(mascarado));
                        } else {
                          setCpfValido(null);
                        }
                      } else if (campo.mascara === 'telefone') {
                        campo.setter(aplicarMascaraTelefone(raw));
                      } else if (campo.mascara === 'moeda') {
                        campo.setter(aplicarMascaraMoeda(raw));
                      } else if (campo.type === 'number') {
                        campo.setter(Number(raw));
                      } else {
                        campo.setter(raw);
                      }
                    }}
                    onBlur={() => {
                      if (isCpf && cpf) {
                        setCpfValido(validarCPF(cpf));
                      }
                    }}
                    step={campo.step}
                    max={campo.max}
                    inputMode={campo.mascara === 'moeda' || campo.mascara === 'cpf' || campo.mascara === 'telefone' ? 'numeric' : undefined}
                    style={{
                      width: '100%',
                      padding: '16px 20px',
                      fontSize: '1.1rem',
                      fontWeight: '600',
                      background: '#f8fafc',
                      border: `2px solid ${borderColor}`,
                      borderRadius: '12px',
                      color: '#1e293b',
                      outline: 'none',
                      transition: 'all 0.3s ease',
                      fontFamily: 'inherit',
                      boxSizing: 'border-box'
                    }}
                  />
                  {campo.suffix && (
                    <span style={{
                      position: 'absolute',
                      right: '20px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#94a3b8',
                      fontSize: '0.95rem',
                      fontWeight: '500',
                      pointerEvents: 'none'
                    }}>
                      {campo.suffix}
                    </span>
                  )}
                </div>
              </div>
            );
            })}
          </div>

        </div>

        {/* Seleção de Taxas por Banco */}
<div className="sim-bancos-section" style={taxaInvalida ? {
  border: '1px solid rgba(239, 68, 68, 0.6)',
  borderRadius: '16px'
} : undefined}>
  <div className="sim-bancos-header">
    <h3 style={{
      fontSize: '1rem',
      fontWeight: '600',
      color: '#e2e8f0',
      margin: 0,
      textTransform: 'uppercase',
      letterSpacing: '0.05em'
    }}>
      📊 Selecione a Taxa por Banco <span style={{ color: '#ef4444' }}>*</span>
    </h3>
    <button
      onClick={() => setTaxasSelecionadas({})}
      style={{
        padding: '8px 16px',
        fontSize: '0.85rem',
        background: 'rgba(239, 68, 68, 0.2)',
        color: '#fca5a5',
        border: '1px solid rgba(239, 68, 68, 0.3)',
        borderRadius: '8px',
        cursor: 'pointer'
      }}
    >
      🗑️ Limpar Seleção
    </button>
  </div>

  {taxaInvalida && (
    <div style={{ color: '#fca5a5', fontSize: '0.85rem', marginBottom: '12px' }}>
      Selecione ao menos uma taxa de banco.
    </div>
  )}

  {taxasLoading && (
    <div style={{ color: '#e2e8f0', fontSize: '0.9rem', padding: '12px 0' }}>
      ⏳ Carregando taxas...
    </div>
  )}

  {!taxasLoading && taxasErro && (
    <div style={{ color: '#fca5a5', fontSize: '0.9rem', padding: '12px 0' }}>
      ⚠️ {taxasErro}{' '}
      <button
        onClick={() => carregarTaxas()}
        style={{
          marginLeft: '8px',
          padding: '6px 12px',
          fontSize: '0.85rem',
          background: 'rgba(96, 165, 250, 0.2)',
          color: '#e2e8f0',
          border: '1px solid rgba(96, 165, 250, 0.5)',
          borderRadius: '8px',
          cursor: 'pointer'
        }}
      >
        Tentar novamente
      </button>
    </div>
  )}

  {!taxasLoading && !taxasErro && bancosApi.length === 0 && (
    <div style={{ color: '#e2e8f0', fontSize: '0.9rem', padding: '12px 0' }}>
      Nenhum banco disponível no momento.
    </div>
  )}

  {bancosApi.map((banco, idx) => (
    <div
      key={banco.codigo}
      className="sim-banco-row"
      style={{
        borderBottom: idx < bancosApi.length - 1 ? '1px solid rgba(197, 18, 18, 0.06)' : 'none',
      }}
    >
      <div className="sim-banco-nome">
        {logoDoBanco(banco.codigo) && (
          <img
            src={logoDoBanco(banco.codigo)}
            alt={banco.nome}
            style={{
              width: '32px',
              height: '32px',
              objectFit: 'contain',
              borderRadius: '6px',
              background: 'white',
              padding: '4px'
            }}
          />
        )}
        <span style={{
          fontWeight: '700',
          fontSize: '0.95rem',
          color: '#ffffff'
        }}>
          {banco.nome}
        </span>
      </div>

      <div className="sim-banco-opcoes">
        {banco.opcoes.map((opcao) => {
          const selecionada = taxasSelecionadas[banco.codigo] === opcao.codigo;
          return (
            <label
              key={opcao.codigo}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer',
                padding: '8px 12px',
                borderRadius: '8px',
                background: selecionada ? 'rgba(96, 165, 250, 0.2)' : 'transparent',
                border: selecionada
                  ? '1px solid rgba(96, 165, 250, 0.5)'
                  : '1px solid transparent',
                transition: 'all 0.2s ease'
              }}
            >
              <input
                type="radio"
                name={`taxa-${banco.codigo}`}
                value={opcao.codigo}
                checked={selecionada}
                onChange={() => {
                  setTaxasSelecionadas(prev => ({
                    ...prev,
                    [banco.codigo]: opcao.codigo
                  }));
                  setTaxaInvalida(false);
                }}
                style={{
                  accentColor: '#60a5fa',
                  width: '16px',
                  height: '16px',
                  cursor: 'pointer'
                }}
              />
              {banco.opcoes.length > 1 && (
                <span style={{ color: '#e2e8f0', fontSize: '0.85rem' }}>
                  {opcao.rotulo}{opcao.sistema ? ` (${opcao.sistema})` : ''}
                </span>
              )}
              <span style={{
                background: 'rgba(148, 163, 184, 0.2)',
                padding: '4px 10px',
                borderRadius: '6px',
                fontSize: '0.8rem',
                fontWeight: '600',
                color: '#94a3b8'
              }}>
                {formatarTaxaAnual(opcao.taxaAnual)}% a.a. + {opcao.indexador}
              </span>
            </label>
          );
        })}
      </div>
    </div>
  ))}

  {!taxasLoading && bancosApi.length > 0 && (
    <div style={{ color: '#94a3b8', fontSize: '0.75rem', marginTop: '12px' }}>
      {vigenteDe && `Taxas atualizadas em ${new Date(vigenteDe).toLocaleDateString('pt-BR')}. `}
      {temIndexadorNaoTR && 'Para linhas que não são TR, o valor simulado é apenas uma estimativa.'}
    </div>
  )}
</div>

        <button
            onClick={simularFinanciamento}
            disabled={loading}
            className="sim-btn-simular"
            style={{
              background: loading
                ? 'rgba(148, 163, 184, 0.5)'
                : 'linear-gradient(135deg, #090e37, #090e37)',
              boxShadow: '0 10px 30px rgba(96, 165, 250, 0.3)',
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
          >
            {loading ? '⏳ Calculando...' : '🚀 Comparar Bancos'}
          </button>

        {error && (
          <div style={{
            marginBottom: '20px',
            padding: '16px',
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '12px',
            color: '#fca5a5'
          }}>
            ⚠️ {error}
          </div>
        )}

        {/* Resultados */}
        {resultado && (
          <>
            {/* Card de Economia */}
            {economia > 0 && (
              <div style={{
                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2), rgba(52, 211, 153, 0.1))',
                borderRadius: '16px',
                padding: '24px',
                marginBottom: '30px',
                border: '1px solid rgba(16, 185, 129, 0.3)',
                textAlign: 'center',
                animation: 'fadeInUp 0.5s ease-out'
              }}>
                <div style={{ fontSize: '1rem', color: '#6ee7b7', marginBottom: '8px' }}>
                  💡 Escolhendo a melhor opção você economiza
                </div>
                <div style={{ fontSize: '2rem', fontWeight: '800', color: '#10b981' }}>
                  {formatarMoeda(economia)}
                </div>
              </div>
            )}

            {/* Botão Exportar PDF */}
              {resultado && (
                <div className="sim-acoes">
                  <button
                    onClick={exportarPDF}
                    style={{
                      padding: '12px 24px',
                      fontSize: '0.95rem',
                      fontWeight: '600',
                      background: 'linear-gradient(135deg, #ee545e, #ee545e)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    📄 Exportar PDF
                  </button>

                  <button
                        onClick={() => setPaginaAtual('evolucao')}
                        style={{
                          padding: '12px 24px',
                          fontSize: '0.95rem',
                          fontWeight: '600',
                          background: 'linear-gradient(135deg, #c4519a, #c4519a)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '10px',
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px',
                        }}
                      >
                        📊 Evolução Comparativa
                    </button>
              
                </div>
              )}


            {/* Comparativo de Bancos */}
            <div
            ref={comparativoRef}
             style={{
              background: 'rgba(255, 255, 255, 0.03)',
              backdropFilter: 'blur(20px)',
              borderRadius: '24px',
              padding: '40px',
              marginBottom: '40px',
              border: '1px solid rgba(255, 255, 255, 0.08)'
            }}>
              <h2 style={{
                fontSize: '1.5rem',
                fontWeight: '700',
                marginBottom: '30px',
                color: '#e2e8f0'
              }}>
                🏦 Comparativo entre Bancos
              </h2>

              <div className="sim-comparativo-grid">
                {resultado.simulacoes.map((sim, idx) => {
                  const isSelected = sim.banco === bancoSelecionado;
                  const isMelhor = sim.totais.totalAPagar === Math.min(...resultado.simulacoes.map(s => s.totais.totalAPagar));

                  return (
                    <div
                      key={idx}
                      onClick={() => setBancoSelecionado(sim.banco)}
                      style={{
                        background: isSelected
                          ? 'linear-gradient(135deg, rgba(96, 165, 250, 0.2), rgba(167, 139, 250, 0.2))'
                          : 'rgba(255, 255, 255, 0.02)',
                        borderRadius: '16px',
                        padding: '24px',
                        border: isSelected
                          ? '2px solid rgba(96, 165, 250, 0.5)'
                          : '1px solid rgba(255, 255, 255, 0.08)',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        position: 'relative'
                      }}
                    >
                      {isMelhor && (
                        <div style={{
                          position: 'absolute',
                          top: '-10px',
                          right: '10px',
                          background: 'linear-gradient(135deg, #10b981, #34d399)',
                          color: 'white',
                          padding: '4px 12px',
                          borderRadius: '20px',
                          fontSize: '0.75rem',
                          fontWeight: '700'
                        }}>
                          ✓ MELHOR
                        </div>
                      )}

                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        marginBottom: '18px'
                      }}>
                        <img
                          src={logosBancos[sim.nomeBanco] || logosBancos[sim.nomeBanco.split(' ')[0]]}
                          alt={sim.nomeBanco}
                          style={{
                            width: '40px',
                            height: '40px',
                            objectFit: 'contain',
                            borderRadius: '8px'
                          }}
                        />
                        <h3 style={{
                          fontSize: '1.15rem',
                          fontWeight: '700',
                          color: '#e2e8f0',
                          margin: 0
                        }}>
                          {sim.nomeBanco}
                        </h3>
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ color: '#94a3b8', fontSize: '0.9rem' }}>Taxa:</span>
                          <span style={{ color: '#f59e0b', fontWeight: '600' }}>{formatarPercentual(sim.taxaJurosAnual)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ color: '#94a3b8', fontSize: '0.9rem' }}>CET:</span>
                          <span style={{ color: '#f97316', fontWeight: '600' }}>{formatarPercentual(sim.cet || 0)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ color: '#94a3b8', fontSize: '0.9rem' }}>1ª Parcela:</span>
                          <span style={{ color: '#60a5fa', fontWeight: '600' }}>{formatarMoeda(sim.informacoes.primeiraParcela)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ color: '#94a3b8', fontSize: '0.9rem' }}>Última:</span>
                          <span style={{ color: '#a78bfa', fontWeight: '600' }}>{formatarMoeda(sim.informacoes.ultimaParcela)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ color: '#94a3b8', fontSize: '0.9rem' }}>Juros:</span>
                          <span style={{ color: '#ef4444', fontWeight: '600' }}>{formatarMoeda(sim.totais.totalJuros)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ color: '#94a3b8', fontSize: '0.9rem' }}>Seguros:</span>
                          <span style={{ color: '#8b5cf6', fontWeight: '600' }}>{formatarMoeda(sim.totais.totalMIP + sim.totais.totalDFI)}</span>
                        </div>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          paddingTop: '12px',
                          borderTop: '1px solid rgba(255,255,255,0.1)',
                          marginTop: '8px'
                        }}>
                          <span style={{ color: '#e2e8f0', fontWeight: '600' }}>Total:</span>
                          <span style={{ color: '#10b981', fontWeight: '700', fontSize: '1.05rem' }}>{formatarMoeda(sim.totais.totalAPagar)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <hr style={{ border: 'none', borderTop: '1px solid rgba(255,255,255,0.1)', margin: '30px 0' }}></hr>

              {/* Gráfico Comparativo */}
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={dadosComparativo}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.1)" />
                  <XAxis dataKey="banco" stroke="#94a3b8" />
                  <YAxis stroke="#94a3b8" tickFormatter={(value) => `R$ ${(value / 1000).toFixed(0)}k`} />
                  <Tooltip
                    contentStyle={{
                      background: 'rgba(15, 23, 42, 0.95)',
                      border: '1px solid rgba(148, 163, 184, 0.2)',
                      borderRadius: '12px',
                      color: '#f1f5f9'
                    }}
                    formatter={(value) => formatarMoeda(value)}
                  />
                  <Legend />
                  <Bar dataKey="totalJuros" name="Total Juros" fill="#ef4444" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="totalAPagar" name="Total a Pagar" fill="#10b981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Detalhes do Banco Selecionado */}
            {simulacaoAtual && (
              <>
                {/* Cards de Resumo */}
                <div className="sim-resumo-grid">
                  {[
                    { label: 'Primeira Parcela', valor: simulacaoAtual.informacoes.primeiraParcela, cor: '#60a5fa', icon: '💰' },
                    { label: 'Última Parcela', valor: simulacaoAtual.informacoes.ultimaParcela, cor: '#a78bfa', icon: '🎯' },
                    { label: 'Total de Juros', valor: simulacaoAtual.totais.totalJuros, cor: '#ef4444', icon: '📊' },
                    { label: 'Total a Pagar', valor: simulacaoAtual.totais.totalAPagar, cor: '#10b981', icon: '💵' },
                    {
                      label: 'Idade Atual/Final',
                      valor: `${simulacaoAtual.informacoes.idadeInicial} → ${simulacaoAtual.informacoes.idadeFinal} anos`,
                      cor: '#f59e0b',
                      icon: '👤',
                      isText: true
                    }
                  ].map((card, idx) => (
                    <div key={idx} style={{
                      background: 'rgba(255, 255, 255, 0.03)',
                      backdropFilter: 'blur(20px)',
                      borderRadius: '16px',
                      padding: '24px',
                      border: '1px solid rgba(255, 255, 255, 0.08)'
                    }}>
                      <div style={{ fontSize: '2rem', marginBottom: '8px' }}>{card.icon}</div>
                      <div style={{ fontSize: '0.85rem', color: '#94a3b8', marginBottom: '8px', fontWeight: '500' }}>
                        {card.label}
                      </div>
                      <div style={{ fontSize: '1.5rem', fontWeight: '700', color: card.cor }}>
                        {card.isText ? card.valor : formatarMoeda(card.valor)}
                      </div>
                    </div>
                  ))}
                </div>
               

                {/* Botão Tabela */}
                <div style={{ textAlign: 'center', marginBottom: '40px' }}>
                  <button
                    onClick={() => setMostrarTabela(!mostrarTabela)}
                    style={{
                      padding: '12px 24px',
                      fontSize: '1.1rem',
                      fontWeight: '600',
                      background: 'linear-gradient(135deg, #c4519a, #ee545e)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      fontFamily: 'inherit'
                    }}
                  >
                    {mostrarTabela ? '📊 Ocultar Tabela' : '📋 Ver Tabela Detalhada'}
                  </button>

                </div>
                  
                {/* Tabela Detalhada */}
                {mostrarTabela && (
                  <div className="sim-tabela-wrap" style={{
                    background: 'rgba(255, 255, 255, 0.03)',
                    backdropFilter: 'blur(20px)',
                    borderRadius: '24px',
                    padding: '40px',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                  }}>
                    <h2 style={{
                      fontSize: '1.5rem',
                      fontWeight: '700',
                      marginBottom: '30px',
                      color: '#e2e8f0'
                    }}>
                      📑 Evolução Detalhada - {simulacaoAtual.nomeBanco}
                    </h2>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                      <thead>
                        <tr style={{
                          background: 'rgba(96, 165, 250, 0.1)',
                          borderBottom: '2px solid rgba(96, 165, 250, 0.3)'
                        }}>
                          {['Nº', 'Amortização', 'Juros', 'MIP', 'DFI', 'TGC', 'Parcela', 'Saldo Devedor'].map((header, idx) => (
                            <th key={idx} style={{
                              padding: '16px 12px',
                              textAlign: 'left',
                              fontWeight: '600',
                              color: '#e2e8f0',
                              whiteSpace: 'nowrap'
                            }}>
                              {header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {simulacaoAtual.parcelas.map((parcela, idx) => (
                          <tr key={idx} style={{
                            borderBottom: '1px solid rgba(148, 163, 184, 0.1)',
                            background: parcela.numero === 0 ? 'rgba(245, 158, 11, 0.1)' : 'transparent'
                          }}>
                            <td style={{ padding: '12px', color: '#94a3b8', fontWeight: '600' }}>
                              {parcela.numero === 0 ? '0 (inicial)' : parcela.numero}
                            </td>
                            <td style={{ padding: '12px', color: '#10b981' }}>{formatarMoeda(parcela.amortizacao)}</td>
                            <td style={{ padding: '12px', color: '#ef4444' }}>{formatarMoeda(parcela.juros)}</td>
                            <td style={{ padding: '12px', color: '#f59e0b' }}>{formatarMoeda(parcela.mip)}</td>
                            <td style={{ padding: '12px', color: '#f59e0b' }}>{formatarMoeda(parcela.dfi)}</td>
                            <td style={{ padding: '12px', color: '#94a3b8' }}>{formatarMoeda(parcela.tgc)}</td>
                            <td style={{ padding: '12px', color: '#60a5fa', fontWeight: '700' }}>{formatarMoeda(parcela.valorParcela)}</td>
                            <td style={{ padding: '12px', color: '#e2e8f0' }}>{formatarMoeda(parcela.saldoDevedor)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* Footer */}
        <div style={{
          textAlign: 'center',
          marginTop: '60px',
          padding: '30px',
          background: 'rgba(255, 255, 255, 0.02)',
          borderRadius: '16px',
          border: '1px solid rgba(255, 255, 255, 0.05)'
        }}>

          <p><strong>Andalafat 2026.</strong> Todos os direitos reservados.
          </p>
          <p style={{ fontSize: '0.9rem', color: '#64748b', lineHeight: '1.6' }}>
            ℹ️  A simulação de financiamento é apenas uma estimativa. As condições reais (valores, taxas e parcelas) só são definidas após a análise de crédito. Custos como avaliação do imóvel podem ser cobrados mesmo em caso de desistência. Todos os valores estão sujeitos a mudanças conforme o perfil do cliente, correções monetárias e regras do banco. Ao apertar em Simular, você confirma que entende esses termos.
          </p>
        </div>
      </div>

    </div>
)}
</>
  );
};

export default SimuladorFinanciamentoAPI;